import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { isUndefined, map } from 'lodash-es';
import { of, switchMap, tap, timer } from 'rxjs';
import { catchError, concatMap, filter } from 'rxjs/operators';

import { CoreConfig } from '../../core.module';
import { AdminPortalApiService } from './adminPortalApi.service';

@Injectable({
  providedIn: 'root'
})
export class LoggerService {
  private coreConfig = inject<CoreConfig>(CoreConfig);
  private adminPortalApiService = inject(AdminPortalApiService);

  private readonly maxFailAttempts = 10;
  private failAttempts = 0;
  private logStack: string[] = [];
  private appName?: string;
  private securityCode?: string;
  private version?: string;

  private userId: string | null = null;

  constructor() {
    timer(600000, 600000).subscribe(() => {
      if (this.failAttempts === this.maxFailAttempts) {
        this.failAttempts = 0;
      }
    });

    timer(1500, 1500)
      .pipe(
        filter(() => !!(this.logStack.length && this.failAttempts < this.maxFailAttempts)),
        concatMap(() => {
          const logs = map(this.logStack, (message) => ({
            hostname: this.coreConfig.loggerHostname,
            level: 'error',
            message: `${message}`
          }));
          this.logStack = [];
          return logs;
        }),
        switchMap((logs) =>
          this.adminPortalApiService
            .sendRequest('POST', '/v1/Logs', {
              body: logs
            })
            .pipe(
              tap(() => {
                this.failAttempts = 0;
              }),
              catchError((error: HttpErrorResponse) => {
                this.failAttempts++;
                this.logStack = [...this.logStack, ...map(logs, 'message')];
                return of(error);
              })
            )
        )
      )
      .subscribe();
  }

  handleError(error: Error) {
    let message = `[AppName: ${this.appName || 'N/A'}]`;
    message += `[Version: ${this.version || 'N/A'}]`;
    message += `[RepCode: ${this.securityCode || 'N/A'}]`;
    message += `[User: ${this.userId || 'N/A'}]`;
    message += ` ${error?.message ?? ''}`;
    if (error?.stack) {
      message += ` Stack: ${error.stack}`;
    }
    this.logStack.push(message);
    throw error;
  }

  updateConfiguration(data: { appName?: string; id?: string | null; securityCode?: string; version?: string }) {
    this.appName = !isUndefined(data.appName) ? data.appName : this.appName;
    this.securityCode = !isUndefined(data.securityCode) ? data.securityCode : this.securityCode;
    this.version = !isUndefined(data.version) ? data.version : this.version;
    this.userId = !isUndefined(data.id) ? data.id : this.userId;
  }
}
